import { useCallback, useEffect, useRef } from 'react'
import { useNotificationStack } from '../../../app/hooks/useNotificationStack'
import { useStartImpersonationSessionMutation } from '../../../app/redux-fetch/apiImpersonation'
import {
  clearPendingStart,
  getPendingStart,
  setImpersonationSessionId,
  setPendingStart,
} from './impersonationSessionStorage'
import messages from './messages'

// E11 R3 (WI 605 start-flow + WI 609 replay). Owns starting an impersonation session under a grant and
// surviving the step-up (RFC-9470 claims-challenge) re-auth redirect.
//
// Happy path (token already carries the c1 auth-context): start() POSTs, gets the session Id, stores it, and
// hard-reloads to '/' so the app re-bootstraps AS the impersonated user (the request interceptor now sends
// X-LogikSpark-Impersonation-Session-Id and /me returns the session → the banner shows).
//
// Step-up path: the POST 401s with a claims challenge; the shared axios response interceptor answers it with
// acquireTokenRedirect (navigating away to MFA). Because we wrote a pending-start record BEFORE the POST, it
// survives the redirect; on return to this page the resume effect re-issues the POST (now with c1) → success.
// The attempt count caps the loop so a token that never satisfies c1 fails loudly instead of redirecting
// forever.
//
// The client start is a convenience/entry-point; the server is authoritative (grant must be active + owned +
// no session already active for it → else 409), and independently enforces step-up.

const MAX_STEPUP_ATTEMPTS = 2 // the initial click + one resume retry after the step-up redirect

// Extracted so tests inject a spy rather than navigating jsdom. A full reload (not SPA nav) is required: the
// entire request identity changes, so all cached data fetched as the real admin must be purged.
export const enterImpersonatedContext = () => window.location.assign('/')

export const useImpersonationStart = (onEntered: () => void = enterImpersonatedContext) => {
  const { enqueueFailure } = useNotificationStack()
  const [startSession, { isLoading }] = useStartImpersonationSessionMutation()
  // Guards the resume effect against React StrictMode's double-invoke (and any re-render) so a pending start
  // is resumed at most once per mount.
  const resumedRef = useRef(false)

  const doStart = useCallback(
    async (grantId: number) => {
      try {
        const sessionId = await startSession({ grantId }).unwrap()
        setImpersonationSessionId(sessionId)
        clearPendingStart()
        onEntered()
      } catch (error) {
        // The base query rejects with { status, data, isClaimsChallenge }. Keep the pending record ONLY when
        // a step-up MFA redirect is genuinely in flight (isClaimsChallenge) — the resume path re-issues the
        // POST after the redirect returns. Everything else (a claims-less 401, a 409 grant-not-active /
        // already-in-session, a network error) is terminal and already toasted by the base query, so clear
        // the pending record; leaving it on a plain 401 would orphan it and spuriously re-POST on the next
        // grants-page visit.
        const isClaimsChallenge = (error as { isClaimsChallenge?: boolean })?.isClaimsChallenge === true
        if (!isClaimsChallenge) {
          clearPendingStart()
        }
      }
    },
    [startSession, onEntered],
  )

  // User action: begin a session for a grant. Records the pending start (attempt 1) BEFORE the POST so it
  // survives the step-up redirect.
  const start = useCallback(
    (grantId: number) => {
      setPendingStart(grantId, 1)
      void doStart(grantId)
    },
    [doStart],
  )

  // Resume after a step-up redirect: on mount, if a start was pending, re-issue it (bumping the attempt
  // count) unless we have exhausted the retry budget.
  useEffect(() => {
    if (resumedRef.current) {
      return
    }
    resumedRef.current = true
    const pending = getPendingStart()
    if (!pending) {
      return
    }
    if (pending.attempts >= MAX_STEPUP_ATTEMPTS) {
      clearPendingStart()
      enqueueFailure(messages.startStepUpFailed)
      return
    }
    setPendingStart(pending.grantId, pending.attempts + 1)
    void doStart(pending.grantId)
    // doStart/enqueueFailure are stable enough; the resumedRef guard makes this effectively run-once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { start, isStarting: isLoading }
}

export default useImpersonationStart
